import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Database, Shield, Globe, FileCheck } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const DataProcessingAgreement = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent">
              Data Processing Agreement
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our commitment to lawful, fair, and transparent processing of your personal data under applicable privacy laws.
          </p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: December 15, 2024 | Last Updated: December 15, 2024</p>
        </div>

        <div className="space-y-8">
          {/* Overview */}
          <Card className="p-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Database className="w-6 h-6 text-blue-500 mr-3" />
              Overview
            </h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                This Data Processing Agreement ("DPA") describes how FlameLink processes personal data in compliance 
                with applicable data protection laws, including the European Union General Data Protection Regulation 
                (GDPR), California Consumer Privacy Act (CCPA), and other regional privacy regulations.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-lg">
                  <Shield className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Lawful Basis</h3>
                  <p className="text-sm text-gray-600">Legal grounds for processing</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <Globe className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Cross-Border</h3>
                  <p className="text-sm text-gray-600">International data transfers</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg">
                  <FileCheck className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Compliance</h3>
                  <p className="text-sm text-gray-600">Multi-jurisdiction adherence</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Definitions */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Definitions</h2>
            
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Data Controller</h3>
                  <p className="text-gray-700 text-sm">
                    FlameLink, Inc. determines the purposes and means of processing personal data for the dating service.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Data Subject</h3>
                  <p className="text-gray-700 text-sm">
                    Individual users of FlameLink whose personal data is processed by our service.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Personal Data</h3>
                  <p className="text-gray-700 text-sm">
                    Any information relating to identified or identifiable individuals using FlameLink.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Processing</h3>
                  <p className="text-gray-700 text-sm">
                    Any operation performed on personal data, including collection, storage, use, and deletion.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Lawful Basis for Processing */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Lawful Basis for Processing</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.1 Contract Performance (Art. 6(1)(b) GDPR)</h3>
                <p className="text-gray-600 mb-3">
                  Processing necessary to provide FlameLink dating services as agreed in our Terms of Service:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Profile creation and matching services</li>
                  <li>• Communication between matched users</li>
                  <li>• Account management and authentication</li>
                  <li>• Premium subscription features</li>
                  <li>• Tipping system functionality</li>
                </ul>
              </div>

              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.2 Legitimate Interest (Art. 6(1)(f) GDPR)</h3>
                <p className="text-gray-600 mb-3">
                  Processing necessary for legitimate business interests, balanced against user rights:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Platform security and fraud prevention</li>
                  <li>• Service improvement and analytics</li>
                  <li>• Customer support and issue resolution</li>
                  <li>• Business operations and administration</li>
                  <li>• Marketing to existing customers (with opt-out)</li>
                </ul>
              </div>

              <div className="border-l-4 border-purple-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.3 Consent (Art. 6(1)(a) GDPR)</h3>
                <p className="text-gray-600 mb-3">
                  Processing based on freely given, specific consent for:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Location tracking for nearby matches</li>
                  <li>• Marketing communications and newsletters</li>
                  <li>• Optional data collection (social media integration)</li>
                  <li>• Cookies for analytics and advertising</li>
                  <li>• Promotional content and special offers</li>
                </ul>
              </div>

              <div className="border-l-4 border-red-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.4 Legal Obligation (Art. 6(1)(c) GDPR)</h3>
                <p className="text-gray-600 mb-3">
                  Processing required to comply with legal obligations:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Age verification (18+ requirement)</li>
                  <li>• Financial record keeping for tax purposes</li>
                  <li>• Law enforcement cooperation when legally required</li>
                  <li>• Regulatory reporting and compliance</li>
                  <li>• Data breach notifications</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Data Processing Categories */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Categories of Personal Data Processed</h2>
            
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">3.1 Identity Data</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Name, username, age</li>
                    <li>• Email address, phone number</li>
                    <li>• Profile photos and videos</li>
                    <li>• Government ID (for verification)</li>
                    <li>• Biometric identifiers (photo verification)</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">3.2 Profile Data</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Bio, interests, preferences</li>
                    <li>• Education, profession</li>
                    <li>• Relationship history and goals</li>
                    <li>• Lifestyle and personality traits</li>
                    <li>• Compatibility preferences</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">3.3 Behavioral Data</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Swipe patterns and preferences</li>
                    <li>• App usage and engagement metrics</li>
                    <li>• Communication patterns</li>
                    <li>• Feature usage statistics</li>
                    <li>• Search and filter activity</li>
                  </ul>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">3.4 Technical Data</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Device information, IP address</li>
                    <li>• Location data (with consent)</li>
                    <li>• App performance and crash logs</li>
                    <li>• Cookie and tracking data</li>
                    <li>• Network and connection info</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">3.5 Financial Data</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Payment card information</li>
                    <li>• Subscription and billing history</li>
                    <li>• Tip offer amounts and history</li>
                    <li>• Transaction records</li>
                    <li>• Refund and dispute information</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">3.6 Communication Data</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Messages between users</li>
                    <li>• Customer support conversations</li>
                    <li>• Push notification preferences</li>
                    <li>• Email communication history</li>
                    <li>• Feedback and survey responses</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* Processing Purposes */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Purposes of Processing</h2>
            
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">4.1 Primary Service Purposes</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• User authentication and account management</li>
                    <li>• Profile creation and matching services</li>
                    <li>• Communication facilitation between users</li>
                    <li>• Compatibility scoring and recommendations</li>
                    <li>• Tipping feature administration</li>
                    <li>• Premium feature provision</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">4.2 Business Purposes</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Service improvement and optimization</li>
                    <li>• Analytics and usage statistics</li>
                    <li>• Customer support and assistance</li>
                    <li>• Marketing and promotional activities</li>
                    <li>• Business administration and operations</li>
                    <li>• Financial management and billing</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">4.3 Security Purposes</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Fraud prevention and detection</li>
                    <li>• Platform security and monitoring</li>
                    <li>• Age and identity verification</li>
                    <li>• Abuse and harassment prevention</li>
                    <li>• Terms of Service enforcement</li>
                    <li>• Risk assessment and mitigation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">4.4 Legal Purposes</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Legal compliance and regulatory requirements</li>
                    <li>• Law enforcement cooperation</li>
                    <li>• Dispute resolution and litigation</li>
                    <li>• Tax and financial reporting</li>
                    <li>• Audit and compliance monitoring</li>
                    <li>• Data protection impact assessments</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* Data Retention */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Data Retention Periods</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">5.1 Retention Schedule</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-medium text-gray-900">Active User Accounts</span>
                    <span className="text-gray-600">Retained while active</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-medium text-gray-900">Deleted User Accounts</span>
                    <span className="text-gray-600">30 days post-deletion</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-medium text-gray-900">Messages and Communications</span>
                    <span className="text-gray-600">Until account deletion</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-medium text-gray-900">Payment and Financial Records</span>
                    <span className="text-gray-600">7 years (legal requirement)</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-medium text-gray-900">Security and Fraud Records</span>
                    <span className="text-gray-600">3 years from incident</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="font-medium text-gray-900">Analytics Data (Anonymized)</span>
                    <span className="text-gray-600">5 years or indefinitely</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">Legal Hold Data</span>
                    <span className="text-gray-600">Until legal matter resolved</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">5.2 Retention Criteria</h3>
                <p className="text-gray-700 mb-4">
                  We determine retention periods based on:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm ml-4">
                  <li>• Purpose for which data was collected</li>
                  <li>• Legal and regulatory requirements</li>
                  <li>• Contractual obligations to users</li>
                  <li>• Legitimate business interests</li>
                  <li>• User expectations and rights</li>
                  <li>• Risk of harm from continued processing</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* International Transfers */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. International Data Transfers</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">6.1 Transfer Mechanisms</h3>
                <p className="text-gray-700 mb-4">
                  FlameLink may transfer personal data internationally using these safeguards:
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Adequacy Decisions</h4>
                    <p className="text-gray-700 text-sm">
                      Transfers to countries deemed adequate by relevant data protection authorities
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Standard Contractual Clauses</h4>
                    <p className="text-gray-700 text-sm">
                      EU-approved contracts ensuring adequate protection levels
                    </p>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Binding Corporate Rules</h4>
                    <p className="text-gray-700 text-sm">
                      Internal policies ensuring consistent global protection
                    </p>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Certification Schemes</h4>
                    <p className="text-gray-700 text-sm">
                      Approved certification programs and codes of conduct
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">6.2 Primary Processing Locations</h3>
                <ul className="space-y-2 text-gray-700 text-sm ml-4">
                  <li>• <strong>United States:</strong> Primary data processing and storage</li>
                  <li>• <strong>European Union:</strong> EU user data processing within EEA</li>
                  <li>• <strong>Canada:</strong> Backup and disaster recovery services</li>
                  <li>• <strong>Australia:</strong> Asia-Pacific user data processing</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Data Subject Rights */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Data Subject Rights</h2>
            
            <div className="space-y-4">
              <p className="text-gray-700">
                Under applicable data protection laws, individuals have the following rights:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Individual Rights</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• <strong>Access:</strong> Obtain copy of personal data</li>
                    <li>• <strong>Rectification:</strong> Correct inaccurate data</li>
                    <li>• <strong>Erasure:</strong> Delete personal data ("right to be forgotten")</li>
                    <li>• <strong>Portability:</strong> Receive data in machine-readable format</li>
                    <li>• <strong>Restriction:</strong> Limit processing under certain conditions</li>
                    <li>• <strong>Objection:</strong> Object to processing for marketing/legitimate interest</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Exercise Process</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• <strong>Request Methods:</strong> Email, in-app settings, support form</li>
                    <li>• <strong>Response Time:</strong> 30 days (extendable by 60 days if complex)</li>
                    <li>• <strong>Identity Verification:</strong> May require proof of identity</li>
                    <li>• <strong>Free of Charge:</strong> No fee for reasonable requests</li>
                    <li>• <strong>Appeal Process:</strong> Contact supervisory authority if dissatisfied</li>
                    <li>• <strong>Automated Decisions:</strong> Right to human review of automated decisions</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* Contact Information */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Contact Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Data Protection Officer</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>Email: dpo@flamelink.com</li>
                  <li>Phone: +1 (555) 123-DATA</li>
                  <li>Response Time: 48 hours</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Privacy Team</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>Email: privacy@flamelink.com</li>
                  <li>Address: 123 Love Street, San Francisco, CA 94102</li>
                  <li>Available: 24/7 for urgent matters</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Contact */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About Data Processing?</h3>
            <p className="text-gray-600 mb-6">
              Contact our Data Protection Officer for any questions about how we process your personal data
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open('mailto:dpo@flamelink.com', '_blank')}
                className="bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white"
              >
                Contact DPO
              </Button>
              <Button
                variant="outline"
                onClick={onBack}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProcessingAgreement;